// frontend/src/presentation/hooks/useNetworkSync.ts
//
// Reaktywny hook Silnika Synchronizacji — warstwa prezentacji.
// Spina stan sieci przeglądarki z warstwą aplikacji (OfflineSyncService):
//  - obserwuje `navigator.onLine` oraz zdarzenia window 'online' / 'offline',
//  - po powrocie sieci automatycznie opróżnia kolejkę operacji offline,
//  - wystawia `triggerSync` do ręcznego wyzwolenia (np. przycisk "Synchronizuj").
//
// UWAGA: to NOWY mechanizm (Clean Architecture), działający RÓWNOLEGLE do
// starego `src/hooks/useNetwork.ts` z dema. Nie zastępuje go ani nie modyfikuje —
// wpięcie w drzewo komponentów nastąpi osobno.

import { useCallback, useEffect, useRef, useState } from 'react'

import { OfflineSyncService } from '../../application/services/OfflineSyncService'
import { HttpSyncApiGateway } from '../../infrastructure/api/HttpSyncApiGateway'
import { db } from '../../infrastructure/offline/AppDatabase'
import { SyncQueueRepository } from '../../infrastructure/repositories/SyncQueueRepository'

/** Wartość zwracana przez {@link useNetworkSync}. */
export interface NetworkSyncState {
  /** Czy przeglądarka raportuje dostęp do sieci (`navigator.onLine`). */
  isOnline: boolean
  /** Czy trwa właśnie przetwarzanie kolejki synchronizacji. */
  isSyncing: boolean
  /** Ręczne wyzwolenie synchronizacji (np. z przycisku w UI). Nigdy nie rzuca. */
  triggerSync: () => Promise<void>
}

/** Fabryka domyślnego, produkcyjnego serwisu: współdzielona baza + bramka HTTP. */
export function createDefaultSyncService(): OfflineSyncService {
  return new OfflineSyncService(new SyncQueueRepository(db), new HttpSyncApiGateway())
}

/** Bezpieczny odczyt `navigator.onLine` (środowiska bez `navigator` → zakładamy online). */
function readOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

/**
 * @param createSyncService fabryka serwisu synchronizacji. Domyślnie buduje
 *   produkcyjny łańcuch (baza IndexedDB + repozytorium + bramka HTTP); w testach
 *   podstawiamy atrapę. Serwis tworzony jest raz, przy pierwszym renderze.
 */
export function useNetworkSync(
  createSyncService: () => OfflineSyncService = createDefaultSyncService,
): NetworkSyncState {
  const [isOnline, setIsOnline] = useState<boolean>(readOnline)
  const [isSyncing, setIsSyncing] = useState<boolean>(false)

  // Serwis budujemy leniwie, raz na cały cykl życia hooka (stabilna tożsamość,
  // brak zależności w efektach).
  const serviceRef = useRef<OfflineSyncService | null>(null)
  if (serviceRef.current === null) {
    serviceRef.current = createSyncService()
  }

  // Zapobiega równoległym przebiegom kolejki (np. zdarzenie 'online' zbiegające
  // się z ręcznym `triggerSync`).
  const runningRef = useRef<boolean>(false)

  const triggerSync = useCallback(async (): Promise<void> => {
    if (runningRef.current) return
    runningRef.current = true
    setIsSyncing(true)
    try {
      await serviceRef.current?.processQueue()
    } catch (error) {
      // OfflineSyncService sam obsługuje brak sieci — tu łapiemy jedynie
      // sytuacje nieoczekiwane, żeby nie wywrócić UI i nie zostawić
      // nieobsłużonego odrzucenia przy automatycznym wyzwoleniu po 'online'.
      console.error('[useNetworkSync] Nieoczekiwany błąd podczas synchronizacji kolejki.', error)
    } finally {
      runningRef.current = false
      setIsSyncing(false)
    }
  }, [])

  useEffect(() => {
    const handleOnline = (): void => {
      setIsOnline(true)
      void triggerSync()
    }
    const handleOffline = (): void => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Domknięcie luki między leniwą inicjalizacją `useState` a podpięciem
    // listenerów — stan sieci mógł się w tym oknie zmienić.
    setIsOnline(readOnline())

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [triggerSync])

  return { isOnline, isSyncing, triggerSync }
}
